import { FormContainer } from '@/components/FormContainer'
import type { TaxRefundMethodEditorViewModel } from '@/features/tax-profile/tax-refund-methods/TaxRefundMethodEditorViewModel'
import { observer } from 'mobx-react-lite'
import {
  FormFieldGroup,
  FormFieldSet,
  FormFieldSetDescription,
  FormFieldSetLegend,
} from '@/components/Form'
import { SelectContainer } from '@/components/inputs/SelectContainer'
import type { BankAccountType } from '../../BankAccount'
import { TextFieldContainer } from '@/components/inputs/TextFieldContainer'
import { PhysicalAddressEditor } from '@/features/tax-profile/components/PhysicalAddressEditor'
import {
  CardItemContent,
  CardList,
  CardListItem,
  CardSubtitle,
  CardTitle,
} from '@/components/Card'
import { useId, useMemo } from 'react'
import { CheckboxContainer } from '@/components/inputs/CheckboxContainer'
import { useViewModelBackedAriaSingleSelection } from '@/hooks/aria-selection-hooks'
import { match } from '@/utils/pattern-match-util'
import { formatTaxDeliveryMethodType } from '@/features/tax-profile/tax-refund-methods/delivery-method-formatter'
import {
  AutoSkeleton,
  IsSkeletonContext,
  useIsSkeleton,
} from '@/components/Skeleton'
import type { TaxRefundDeliveryMethodType } from '@/features/tax-profile/tax-refund-methods/TaxRefundDeliveryMethod'

const bankAccountTypes: { key: BankAccountType; label: string }[] = [
  { key: 'Checking', label: 'Checking' },
  { key: 'Savings', label: 'Savings' },
]

export interface TaxRefundMethodEditorProps {
  formId?: string
  isDisabled?: boolean
  isSkeleton?: boolean
  vm: TaxRefundMethodEditorViewModel
}

/**
 * The TaxRefundMethodEditor
 */
export const TaxRefundMethodEditor = observer(function TaxRefundMethodEditor({
  formId,
  isDisabled,
  isSkeleton,
  vm,
}: TaxRefundMethodEditorProps) {
  const testId = 'tax-refund-method-editor'
  const methodLegendId = useId()
  isSkeleton = useIsSkeleton(isSkeleton)
  const methodItems = useMemo(
    () =>
      vm.availableDeliveryMethodTypes.map((type) => ({
        type,
        label: formatTaxDeliveryMethodType(type),
      })),
    [vm.availableDeliveryMethodTypes],
  )

  const selectionProps = useViewModelBackedAriaSingleSelection({
    disallowEmptySelection: true,
    items: vm.availableDeliveryMethodTypes,
    selectedItem: vm.methodType,
    getKey: (i) => i,
    onSelectionChange: (selected) => (vm.methodType = selected),
  })

  return (
    <IsSkeletonContext value={isSkeleton}>
      <FormContainer
        id={formId}
        onSubmit={vm.save}
        disabled={vm.save.pending || vm.isReadOnly || isDisabled}
        testId={testId}
      >
        {!vm.isReadOnly && (
          <FormFieldSet>
            <FormFieldSetLegend id={methodLegendId} isSkeleton={isSkeleton}>
              Choose your method
            </FormFieldSetLegend>
            <CardList
              escapeKeyBehavior="none"
              aria-labelledby={methodLegendId}
              items={methodItems}
              {...selectionProps}
            >
              {({ label, type }) => {
                const itemTestId = testId
                  ? `${testId}-option-${type}`
                  : undefined
                return (
                  <AvailableDeliverMethodTypeItemContainer
                    id={type}
                    type={type}
                    label={label}
                    testId={itemTestId}
                  />
                )
              }}
            </CardList>
          </FormFieldSet>
        )}

        {vm.methodType === 'DirectDeposit' && (
          <DirectDepositForm vm={vm} testId={testId} />
        )}
        {vm.methodType === 'Check' && <CheckForm vm={vm} testId={testId} />}
        {vm.methodType === 'FutureTaxCredit' && <FutureTaxCreditForm vm={vm} />}
      </FormContainer>
    </IsSkeletonContext>
  )
})

interface AvailableDeliverMethodTypeItemContainerProps {
  id: string
  label: string
  type: TaxRefundDeliveryMethodType
  testId?: string
}

/**
 * Available Deliver Method Type Item Container
 */
const AvailableDeliverMethodTypeItemContainer = observer(
  function AvailableDeliverMethodTypeItemContainer({
    label,
    type,
    testId,
  }: AvailableDeliverMethodTypeItemContainerProps) {
    // Passing `isSkeleton` as a prop doesn't cause a rerender due to how
    // React Aria collections work, so using context is the simplest solution.
    const isSkeleton = useIsSkeleton()
    return (
      <CardListItem id={type} textValue={label} testId={testId}>
        <CardItemContent isSkeleton={isSkeleton}>
          <CardTitle>
            <AutoSkeleton isSkeleton={isSkeleton}>{label}</AutoSkeleton>
          </CardTitle>
          <CardSubtitle variant="sm">
            <AutoSkeleton isSkeleton={isSkeleton}>
              {match(type, {
                DirectDeposit: () => (
                  <>
                    Provide your personal bank account information upfront. The
                    tax preparer will share your account details with the IRS,
                    to pay out refunds automatically.
                  </>
                ),
                Check: () => (
                  <>
                    Receive a refund as a paper check. This option takes longer
                    to process.
                  </>
                ),
                FutureTaxCredit: () => (
                  <>
                    Apply your refund towards future estimated tax payments
                    rather than receiving it as cash. Useful if you expect to
                    owe taxes in the next quarter or year.
                  </>
                ),
              })}
            </AutoSkeleton>
          </CardSubtitle>
        </CardItemContent>
      </CardListItem>
    )
  },
)

interface DirectDepositFormProps {
  vm: TaxRefundMethodEditorViewModel
  testId?: string
}

const DirectDepositForm = observer(function DirectDepositForm({
  vm,
  testId,
}: DirectDepositFormProps) {
  return (
    <FormFieldSet>
      <FormFieldSetLegend>Direct deposit</FormFieldSetLegend>

      <FormFieldSetDescription>
        {vm.isReadOnly ? (
          <>The IRS will deposit your refund directly into your bank account.</>
        ) : (
          <>
            Enter the information for the bank account that you would like to
            receive your IRS refund to.
          </>
        )}
      </FormFieldSetDescription>
      <FormFieldGroup>
        <SelectContainer
          label="Account type"
          model={vm.directDepositVm.bankAccountVm}
          field="accountType"
          items={bankAccountTypes}
          getKey={(c) => c.key}
          getLabel={(c) => c.label}
          testId={testId ? `${testId}-account-type-select` : undefined}
        />
      </FormFieldGroup>
      <FormFieldGroup>
        <TextFieldContainer
          model={vm.directDepositVm.bankAccountVm}
          field="bankName"
          label="Bank name"
          placeholder="e.g Bank of America"
          testId={testId ? `${testId}-bank-name-input` : undefined}
        />
      </FormFieldGroup>
      <FormFieldGroup stackWhenCompact>
        <TextFieldContainer
          model={vm.directDepositVm.bankAccountVm}
          field="routingNumber"
          label="Routing number"
          placeholder="e.g 1234567890"
          data-private
          testId={testId ? `${testId}-routing-number-input` : undefined}
        />
        <TextFieldContainer
          model={vm.directDepositVm.bankAccountVm}
          field="accountNumber"
          label="Account number"
          placeholder="e.g 0987654321"
          data-private
          testId={testId ? `${testId}-account-number-input` : undefined}
        />
      </FormFieldGroup>
    </FormFieldSet>
  )
})

interface CheckFormProps {
  vm: TaxRefundMethodEditorViewModel
  testId?: string
}

const CheckForm = observer(function CheckForm({ vm, testId }: CheckFormProps) {
  return (
    <FormFieldSet>
      <FormFieldSetLegend>Paper check by mail</FormFieldSetLegend>
      <FormFieldSetDescription>
        {vm.isReadOnly ? (
          <>The IRS will mail a paper check to your address.</>
        ) : (
          <>
            Enter the mailing address where you would like to receive your IRS
            refund check.
          </>
        )}
      </FormFieldSetDescription>
      <CheckboxContainer
        model={vm.checkVm}
        field="useProfileAddress"
        label="Use same address as contact information"
        testId={testId ? `${testId}-use-profile-address-checkbox` : undefined}
      />
      {!vm.checkVm.useProfileAddress && (
        <PhysicalAddressEditor
          vm={vm.checkVm.mailingAddressVm}
          testId={testId ? `${testId}-mailing-address` : undefined}
        />
      )}
    </FormFieldSet>
  )
})

interface FutureTaxCreditFormProps {
  vm: TaxRefundMethodEditorViewModel
}

const FutureTaxCreditForm = observer(function FutureTaxCreditForm({
  vm: _,
}: FutureTaxCreditFormProps) {
  return (
    <FormFieldSet>
      <FormFieldSetLegend>Future tax credit</FormFieldSetLegend>
      <FormFieldSetDescription>
        Your refund will be applied to your future estimated tax payments.
      </FormFieldSetDescription>
    </FormFieldSet>
  )
})
